import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../redux/store';
import { CategoryValue, LocationValue, LovValue, PersonelValue } from '../utils/common';
import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';

export const useLovSelectOptions = (key: string) => {
  return useSelector((state: RootState) =>
    state.lov.values
      .filter((item: LovValue) => item.key === key)
      .map((item) => ({
        label: item.label,
        value: item.value,
      }))
  );
};

export const useLovLocationOptions = (key: String) => {
  return useSelector((state: RootState) =>
    state.location.values
      .filter((item: LocationValue) => item.key === key)
      .map((item) => ({
        label: item.label,
        value: item.label,
        ilId: item.ilId,
        ilceId: item.ilceId
      }))
  );
}

export const useLovPersonelOptions = (key: string) => {
  return useSelector((state: RootState) =>
    state.personel.values
      .filter((item: PersonelValue) => item.type === key)
      .map((item) => ({
        label: item.name,
        value: item.id,
      }))
  );
};

export const useLovPersonelOptionsAll = () => {
  return useSelector((state: RootState) =>
    state.personel.values
      .map((item) => ({
        label: item.name,
        value: item.id,
      }))
  );
};

export const useCategoryOptions = (key: string) => {
  return useSelector((state: RootState) =>
    state.category.values
      .filter((item: CategoryValue) => item.type === key)
      .map((item) => ({
        key: item.key,
        title: item.title,
        selectable: item.selectable,
        children: item.children
      }))
  );
};

export const useLovLabelMap = (key: string): Record<string, string> => {
  const lovItems = useSelector((state: RootState) =>
    state.lov.values.filter((item) => item.key === key)
  );
  return Object.fromEntries(lovItems.map(item => [item.value, item.label]));
};

export const useLovJobOptions = () => {
  return useSelector((state: RootState) =>
    state.job.values
      .map((item) => ({
        label: item.label,
        value: item.value,
      }))
  );
};

export const useDynamicTitleFromSidebar = () => {
  const location = useLocation();
  const { items } = useSelector((state: RootState) => state.menu) as { items: Array<{ path: string; label: string; children?: Array<{ path: string; label: string }> }> };

  useEffect(() => {
    let matchedLabel: string | null = null;

    for (const item of items) {
      // Ana menüde eşleşme kontrolü
      if (item.path === location.pathname) {
        matchedLabel = item.label;
        break;
      }

      // Alt menüde eşleşme kontrolü
      if (item.children) {
        const childMatch = item.children.find((child: any) => child.path === location.pathname);
        if (childMatch) {
          matchedLabel = childMatch.label;
          break;
        }
      }
    }

    if (matchedLabel && typeof matchedLabel === 'string') {
      document.title = matchedLabel;
    } else {
      document.title = `ISG Platform`;
    }
  }, [location.pathname, items]);
};

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;